import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { sanitizeHtml } from '../utils/sanitize';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Container,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { resolveEventTheme, eventSurfaceSx } from '../eventThemes';
import { CheckCircleOutline, CalendarToday, LocationOn } from '@mui/icons-material';
import { Event } from '../types';
import EventNavigation from './EventNavigation';


interface RSVPFormData {
  name: string;
  email_address: string;
  attending: string;
  bringing_guests: string;
  guest_count: number;
  guest_names: string[];
  items_bringing: string[];
  other_items: string;
}

const parseOtherItems = (value?: string): string[] => {
  if (!value) return [];
  return value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const RSVPForm: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const baseTheme = useTheme();
  const [formData, setFormData] = useState<RSVPFormData>({
    name: '',
    email_address: '',
    attending: '',
    bringing_guests: '',
    guest_count: 1,
    guest_names: [],
    items_bringing: [],
    other_items: ''
  });
  const [neededItems, setNeededItems] = useState<string[]>([]);
  const [claimedItems, setClaimedItems] = useState<string[]>([]);
  const [claimedOtherItems, setClaimedOtherItems] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [editLink, setEditLink] = useState<string | null>(null);
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [isItemsSelectOpen, setIsItemsSelectOpen] = useState(false);

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        const [eventResponse, rsvpsResponse] = await Promise.all([
          axios.get<Event>(`/api/events/${slug}`),
          axios.get(`/api/events/${slug}/rsvps`)
        ]);

        if (eventResponse.data.rsvp_cutoff_date) {
          const cutoffDate = new Date(eventResponse.data.rsvp_cutoff_date);
          if (new Date() > cutoffDate) {
            navigate(`/view/events/${slug}`);
            return;
          }
        }

        let items: string[] = [];
        if (eventResponse.data.needed_items) {
          items = Array.isArray(eventResponse.data.needed_items)
            ? eventResponse.data.needed_items
            : typeof eventResponse.data.needed_items === 'string'
              ? JSON.parse(eventResponse.data.needed_items)
              : [];
        }

        const claimed = new Set<string>();
        const claimedOther = new Set<string>();
        if (Array.isArray(rsvpsResponse.data)) {
          rsvpsResponse.data.forEach((rsvp: any) => {
            try {
              let rsvpItems: string[] = [];
              if (typeof rsvp.items_bringing === 'string') {
                try {
                  const parsed = JSON.parse(rsvp.items_bringing);
                  rsvpItems = Array.isArray(parsed) ? parsed : [];
                } catch (e) {
                  console.error('Error parsing items_bringing JSON:', e);
                }
              } else if (Array.isArray(rsvp.items_bringing)) {
                rsvpItems = rsvp.items_bringing;
              }
              if (Array.isArray(rsvpItems)) {
                rsvpItems.forEach((item: string) => claimed.add(item));
              }
              parseOtherItems(typeof rsvp.other_items === 'string' ? rsvp.other_items : '').forEach((item) => claimedOther.add(item));
            } catch (e) {
              console.error('Error processing RSVP items:', e);
            }
          });
        }

        const availableItems = items.filter(item => !claimed.has(item)).sort((a, b) => a.localeCompare(b));
        setNeededItems(availableItems);
        setClaimedItems(Array.from(claimed).sort((a, b) => a.localeCompare(b)));
        setClaimedOtherItems(Array.from(claimedOther).sort((a, b) => a.localeCompare(b)));
        setEvent(eventResponse.data);
      } catch (error) {
        console.error('Error fetching event details:', error);
        setError('Failed to load event details');
        setNeededItems([]);
      }
    };
    fetchEventDetails();
  }, [slug]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'attending') {
      if (value === 'no' || value === 'maybe') {
        setFormData(prev => ({
          ...prev, [name]: value,
          bringing_guests: 'no', guest_count: 0, guest_names: [],
          items_bringing: [], other_items: ''
        }));
        return;
      }
    }

    if (name === 'bringing_guests') {
      if (value === 'no') {
        setFormData(prev => ({ ...prev, [name]: value, guest_count: 0, guest_names: [] }));
        return;
      } else if (value === 'yes') {
        setFormData(prev => ({ ...prev, [name]: value, guest_count: 1, guest_names: [''] }));
        return;
      }
    }

    if (name === 'guest_count') {
      const count = parseInt(value) || 0;
      setFormData(prev => ({
        ...prev, [name]: count,
        guest_names: Array(count).fill('').map((_, i) => prev.guest_names[i] || '')
      }));
      return;
    }

    if (name.startsWith('guest_name_')) {
      const index = parseInt(name.split('_')[2]);
      setFormData(prev => ({
        ...prev,
        guest_names: prev.guest_names.map((n, i) => i === index ? value : n)
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;

    if (name === 'attending' && value !== 'yes') {
      setFormData(prev => ({
        ...prev, attending: value as 'no' | 'maybe',
        bringing_guests: 'no', guest_count: 0, guest_names: [],
        items_bringing: [], other_items: ''
      }));
    } else if (name === 'bringing_guests') {
      setFormData(prev => {
        const maxGuests = event?.max_guests_per_rsvp;
        let initialGuestCount = 1;
        if (maxGuests === 0 && value === 'yes') {
          return { ...prev, bringing_guests: 'no', guest_count: 0, guest_names: [] };
        }
        if (maxGuests !== undefined && maxGuests !== -1 && maxGuests < initialGuestCount) {
          initialGuestCount = maxGuests;
        }
        return {
          ...prev, bringing_guests: value as 'yes' | 'no',
          guest_count: value === 'yes' ? initialGuestCount : 0,
          guest_names: value === 'no' ? [] : Array(initialGuestCount).fill('')
        };
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleItemsChange = (e: SelectChangeEvent<string[]>) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, items_bringing: Array.isArray(value) ? value : [] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    if (!formData.name.trim() || !formData.email_address.trim() || !formData.attending) {
      setError('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    if (formData.attending === 'yes' && !formData.bringing_guests) {
      setError('Please indicate if you are bringing guests');
      setIsSubmitting(false);
      return;
    }

    if (formData.bringing_guests === 'yes' &&
        (formData.guest_count < 1 || formData.guest_names.some(name => !name.trim()))) {
      setError('Please provide names for all guests');
      setIsSubmitting(false);
      return;
    }

    try {
      const splitOtherItems = formData.other_items
        .split(/\r?\n|,/).map(s => s.trim()).filter(Boolean).join(', ');
      const submissionData = {
        ...formData,
        items_bringing: formData.items_bringing,
        other_items: splitOtherItems,
        send_email_confirmation: true,
        email_address: formData.email_address.trim(),
        send_event_conclusion_email: true,
      };
      const response = await axios.post(`/api/events/${slug}/rsvp`, submissionData);
      if (response.status !== 201) {
        throw new Error(`Unexpected RSVP response status: ${response.status}`);
      }
      const editId = response.data?.edit_id;
      setEditLink(typeof editId === 'string' && editId.length > 0 && slug
        ? `/events/${encodeURIComponent(slug)}/rsvp/edit/${encodeURIComponent(editId)}`
        : null);
      setSuccess(true);
    } catch (err) {
      console.error('Error submitting RSVP:', err);
      setError('Failed to submit RSVP. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const eventTheme = useMemo(() => resolveEventTheme(baseTheme, event || {}), [event, baseTheme]);

  const wpPos = (event?.wallpaper_position || '50% 50%').split(/\s+/);
  const wpX = Number.isFinite(parseFloat(wpPos[0])) ? parseFloat(wpPos[0]) : 50;
  const wpY = Number.isFinite(parseFloat(wpPos[1])) ? parseFloat(wpPos[1]) : 50;
  const wpZoom = event?.wallpaper_zoom ?? 1;

  const rootSx = {
    ...eventSurfaceSx(event), minHeight: '100vh',
  };

  if (success) {
    const successContent = (
      <Box sx={rootSx}>
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
            {event?.wallpaper && (
              <Box sx={{ width: '100%', aspectRatio: '16 / 5', overflow: 'hidden', position: 'relative', borderRadius: 2, mb: 2 }}>
                <Box component="img" src={event.wallpaper} alt={event.title} sx={{
                  position: 'absolute', left: `${wpX}%`, top: `${wpY}%`,
                  transform: `translate(-${wpX}%, -${wpY}%)`,
                  width: `${100 * wpZoom}%`, height: 'auto', display: 'block',
                }} />
              </Box>
            )}
            <CheckCircleOutline sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" sx={{ mb: 1 }}>
              Thank you!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              Your RSVP has been submitted successfully.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: editLink ? 2 : 4 }}>
              A confirmation email is on its way! Please make sure to check your junk/spam folder if you don't see it in your inbox.
            </Typography>
            {editLink && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Save this edit link in case your email is delayed:
                </Typography>
                <Button component="a" href={editLink} variant="text" sx={{ overflowWrap: 'anywhere' }}>
                  Edit your RSVP
                </Button>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}><EventNavigation slug={slug || ''} page="rsvp" /></Box>
          </Paper>
        </Container>
      </Box>
    );
    return eventTheme ? <ThemeProvider theme={eventTheme}>{successContent}</ThemeProvider> : successContent;
  }

  const mainContent = (
    <Box sx={rootSx}>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}><EventNavigation slug={slug || ''} page="rsvp" /></Box>
        {/* Event details hero card */}
        {event && (
          <Paper sx={{ p: 3, mb: 3 }}>
            {event.wallpaper && (
              <Box sx={{ width: '100%', aspectRatio: '16 / 5', overflow: 'hidden', position: 'relative', borderRadius: 2, mb: 2 }}>
                <Box component="img" src={event.wallpaper} alt={event.title} sx={{
                  position: 'absolute', left: `${wpX}%`, top: `${wpY}%`,
                  transform: `translate(-${wpX}%, -${wpY}%)`,
                  width: `${100 * wpZoom}%`, height: 'auto', display: 'block',
                }} />
              </Box>
            )}
            <Typography variant="h4" sx={{ mb: 2 }}>
              {event.title}
            </Typography>
            <Stack spacing={0.75} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary">
                  {new Date(event.date).toLocaleDateString(undefined, {
                    weekday: 'short', month: 'short', day: 'numeric',
                    year: 'numeric', hour: 'numeric', minute: '2-digit',
                  })}
                </Typography>
              </Box>
              {event.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary">
                    {event.location}
                  </Typography>
                </Box>
              )}
            </Stack>
            {event.description && (
              <Box
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  '& *': { maxWidth: '100%' },
                  '& p': { margin: 0, lineHeight: 1.6, whiteSpace: 'normal' },
                  '& p + p': { marginTop: '0.75em' },
                  '& p:empty': { marginTop: '0.5em' },
                  '& br': { display: 'block', content: '""', marginTop: '0.5em' },
                  '& ul, & ol': { marginLeft: 2, marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '1.5em' },
                  '& li': { marginBottom: 0, lineHeight: 1.6 },
                  '& h1, & h2, & h3': { marginTop: '0.75em', marginBottom: '0.5em' },
                  '& a': { color: 'primary.main' },
                  '& img': { maxWidth: '100%', height: 'auto' },
                  '& pre, & code': { whiteSpace: 'pre-wrap', maxWidth: '100%', overflow: 'auto' },
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }}
              />
            )}
            {event.rsvp_cutoff_date && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                RSVPs close on {new Date(event.rsvp_cutoff_date).toLocaleDateString(undefined, {
                  weekday: 'short', month: 'short', day: 'numeric',
                  year: 'numeric', hour: 'numeric', minute: '2-digit',
                })}
              </Typography>
            )}
          </Paper>
        )}

        {/* RSVP form card */}
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            Your RSVP
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
              />

              <TextField
                label="Email Address"
                name="email_address"
                type="email"
                value={formData.email_address}
                onChange={handleChange}
                required
                fullWidth
                helperText="You will receive a confirmation email with an edit link"
              />

              <FormControl fullWidth required>
                <InputLabel>Are you attending?</InputLabel>
                <Select
                  name="attending"
                  value={formData.attending}
                  onChange={handleSelectChange}
                  label="Are you attending?"
                >
                  <MenuItem value="">Select an option</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>

              {formData.attending === 'yes' && (
                <>
                  <FormControl fullWidth required>
                    <InputLabel>Are you bringing any guests?</InputLabel>
                    <Select
                      name="bringing_guests"
                      value={formData.bringing_guests}
                      onChange={handleSelectChange}
                      label="Are you bringing any guests?"
                    >
                      <MenuItem value="">Select an option</MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>

                  {formData.bringing_guests === 'yes' && (
                    <>
                      {event?.max_guests_per_rsvp !== undefined && event?.max_guests_per_rsvp !== -1 ? (
                        <FormControl fullWidth required>
                          <InputLabel>Number of Guests</InputLabel>
                          <Select
                            name="guest_count"
                            value={formData.guest_count.toString()}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              setFormData(prev => ({
                                ...prev, guest_count: value,
                                guest_names: Array(value).fill('').map((_, i) => prev.guest_names[i] || '')
                              }));
                            }}
                            label="Number of Guests"
                          >
                            {Array.from({ length: event.max_guests_per_rsvp }, (_, i) => i + 1).map((num) => (
                              <MenuItem key={num} value={num.toString()}>{num}</MenuItem>
                            ))}
                          </Select>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                            Maximum {event.max_guests_per_rsvp} additional guests allowed
                          </Typography>
                        </FormControl>
                      ) : (
                        <TextField
                          label="Number of Guests"
                          name="guest_count"
                          value={formData.guest_count}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!/^\d*$/.test(value)) return;
                            const numValue = parseInt(value) || 0;
                            const newCount = value === '' ? 0 : Math.max(1, numValue);
                            setFormData(prev => ({
                              ...prev, guest_count: newCount,
                              guest_names: Array(newCount).fill('').map((_, i) => prev.guest_names[i] || '')
                            }));
                          }}
                          fullWidth
                          required
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                          error={formData.guest_count < 1}
                          helperText={formData.guest_count < 1 ? "Number of guests must be at least 1" : "No limit on number of guests"}
                        />
                      )}

                      {Array.from({ length: formData.guest_count }).map((_, index) => (
                        <TextField
                          key={index}
                          fullWidth
                          label={`Guest ${index + 1} Name`}
                          name={`guest_name_${index}`}
                          value={formData.guest_names[index] || ''}
                          onChange={handleChange}
                          required
                        />
                      ))}
                    </>
                  )}

                  {(claimedItems.length > 0 || claimedOtherItems.length > 0) && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Items already being brought:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Array.from(new Set([...claimedItems, ...claimedOtherItems]))
                          .sort((a, b) => a.localeCompare(b))
                          .map((item) => (
                          <Chip key={item} label={item} color="success" size="small" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {neededItems.length > 0 && (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>What items are you bringing?</InputLabel>
                        <Select
                          multiple
                          name="items_bringing"
                          value={formData.items_bringing}
                          onChange={handleItemsChange}
                          input={<OutlinedInput label="What items are you bringing?" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" />
                              ))}
                            </Box>
                          )}
                          open={isItemsSelectOpen}
                          onOpen={() => setIsItemsSelectOpen(true)}
                          onClose={() => setIsItemsSelectOpen(false)}
                          MenuProps={{
                            PaperProps: { sx: { maxHeight: 300, overflowY: 'auto' } },
                          }}
                        >
                          {neededItems.map((item) => (
                            <MenuItem key={item} value={item}>
                              <Checkbox checked={formData.items_bringing.includes(item)} />
                              <ListItemText primary={item} />
                            </MenuItem>
                          ))}
                          <Box sx={{
                            position: 'sticky', bottom: 0, bgcolor: 'background.paper',
                            p: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center',
                          }}>
                            <Button variant="contained" onClick={() => setIsItemsSelectOpen(false)} fullWidth size="small">
                              Done
                            </Button>
                          </Box>
                        </Select>
                      </FormControl>
                    </>
                  )}

                  <TextField
                    label="Any other item(s)?"
                    name="other_items"
                    value={formData.other_items}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Enter any additional items you'd like to bring"
                  />
                </>
              )}

              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting ||
                    !formData.name.trim() || !formData.email_address.trim() || !formData.attending ||
                    (formData.attending === 'yes' && !formData.bringing_guests) ||
                    (formData.bringing_guests === 'yes' && (formData.guest_count < 1 || formData.guest_names.some(n => !n.trim())))
                  }
                  sx={{ flex: 1 }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit RSVP'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );

  return eventTheme ? <ThemeProvider theme={eventTheme}>{mainContent}</ThemeProvider> : mainContent;
};

export default RSVPForm;
